import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useAuth } from '@pooflabs/web';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { setRoundsPicks } from '@/lib/collections/rounds';
import type { RoundsResponse, RoundsRacersResponse, RoundsPicksResponse } from '@/lib/collections/rounds';
import { Address } from '@/lib/db-client';
import { CoinCharacter } from './CoinCharacter';
import { CHOOSING_CUTOFF_SEC, POINTS_PER_WIN } from '@/lib/constants';

const CHOOSING_CUTOFF = parseInt(CHOOSING_CUTOFF_SEC, 10); // 300s = 5 minutes
const POINTS_WIN_MAX = parseInt(POINTS_PER_WIN, 10); // 100

/**
 * Compute points the user would earn if they pick RIGHT NOW and win.
 * Formula: 100 - 3 * floor(secondsSincePickStart / 30), floored at 55.
 */
function computeCurrentWinPoints(nowSec: number, raceStartTime: number): number {
  const secondsSincePickStart = Math.max(0, nowSec - raceStartTime);
  const windows = Math.floor(secondsSincePickStart / 30);
  return Math.max(55, POINTS_WIN_MAX - 3 * windows);
}

function formatPickLockCountdown(seconds: number): string {
  if (seconds <= 0) return '0s left';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins > 0) return `${mins}m ${secs.toString().padStart(2, '0')}s left`;
  return `${secs}s left`;
}

const COIN_COLORS = [
  '#ff3da8', '#fbbf24', '#34d399', '#60a5fa', '#a78bfa', '#fb7185',
];

interface RacePanelProps {
  round: RoundsResponse;
  racers: RoundsRacersResponse[];
  userPick: RoundsPicksResponse | null;
  secondsLeft: number;
  choosingOpen: boolean;
}

function clamp(val: number, min: number, max: number) {
  return Math.max(min, Math.min(max, val));
}

/**
 * Compute normalized track positions for all contestants.
 * Leader → ~90%, last place → ~10%, others linearly interpolated.
 * Edge cases: single racer → 90%; all equal scores → 50%.
 */
function getRelativePositions(racers: RoundsRacersResponse[]): Map<string, number> {
  const result = new Map<string, number>();
  if (racers.length === 0) return result;

  const scores = racers.map((r) => parseFloat(r.performancePct) || 0);
  const maxScore = Math.max(...scores);
  const minScore = Math.min(...scores);

  if (racers.length === 1) {
    result.set(racers[0].coinId, 83);
    return result;
  }

  if (maxScore === minScore) {
    for (const r of racers) result.set(r.coinId, 50);
    return result;
  }

  for (let i = 0; i < racers.length; i++) {
    const normalized = (scores[i] - minScore) / (maxScore - minScore); // 0–1
    // Cap at 83% so the leader's coin + rocket trail never reaches the lane's right edge,
    // preventing clipping from the outer overflow-hidden container on narrow mobile screens.
    const pos = clamp(10 + normalized * 73, 10, 83); // map to 10%–83%
    result.set(racers[i].coinId, pos);
  }

  return result;
}

export const RacePanel: React.FC<RacePanelProps> = ({
  round,
  racers,
  userPick,
  secondsLeft,
  choosingOpen,
}) => {
  const { user, login } = useAuth();
  const [picking, setPicking] = useState(false);

  // Live clock for "points if you win" indicator — updates every second
  const [nowSec, setNowSec] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    if (!choosingOpen) return;
    const id = setInterval(() => setNowSec(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, [choosingOpen]);

  const pointsIfWin = computeCurrentWinPoints(nowSec, round.raceStartTime);
  // Points the user locked in when they placed their pick (shown after confirming)
  const lockedPoints = userPick
    ? computeCurrentWinPoints(userPick.pickedAt, round.raceStartTime)
    : POINTS_WIN_MAX;

  const handlePick = async (coinId: string) => {
    if (!user) {
      login();
      return;
    }
    setPicking(true);
    try {
      const success = await setRoundsPicks(round.roundId, user.address, {
        userAddress: Address.publicKey(user.address),
        coinId,
        pickedAt: Math.floor(Date.now() / 1000),
      });
      if (success) {
        const racer = racers.find((r) => r.coinId === coinId);
        toast.success(`✅ You picked ${racer?.symbol ?? coinId} — good luck! 🚀`);
      } else {
        toast.error('Could not place pick. Picks may be locked!');
      }
    } catch {
      toast.error('Something went wrong placing your pick.');
    } finally {
      setPicking(false);
    }
  };

  const sortedRacers = useMemo(() => {
    return [...racers].sort(
      (a, b) => parseFloat(b.performancePct) - parseFloat(a.performancePct)
    );
  }, [racers]);

  // Map coinId -> 1-based race position (1 = leader, N = last)
  const raceRanks = useMemo(() => {
    const map = new Map<string, number>();
    sortedRacers.forEach((racer, idx) => {
      map.set(racer.coinId, idx + 1);
    });
    return map;
  }, [sortedRacers]);

  // Track peak (max) and trough (min) performancePct observed per coin during this race
  // session. These are used as the tiebreaker when multiple coins share the top or
  // bottom final score: highest peak wins 😄, lowest trough loses 😢.
  const peakPerfRef = useRef<Map<string, number>>(new Map());
  const troughPerfRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    for (const racer of racers) {
      const perf = parseFloat(racer.performancePct) || 0;
      const currentPeak = peakPerfRef.current.get(racer.coinId);
      const currentTrough = troughPerfRef.current.get(racer.coinId);

      if (currentPeak === undefined || perf > currentPeak) {
        peakPerfRef.current.set(racer.coinId, perf);
      }
      if (currentTrough === undefined || perf < currentTrough) {
        troughPerfRef.current.set(racer.coinId, perf);
      }
    }
  }, [racers]);

  // Tiebreaker: among coins sharing the top final score, the one that hit the
  // HIGHEST value at any point during the round wins 😄. Among coins sharing the
  // bottom final score, the one that hit the LOWEST value at any point loses 😢.
  // If even peak/trough values tie, fall back to stable coin array order.
  const { winnerId, loserId } = useMemo(() => {
    if (racers.length <= 1) {
      return { winnerId: racers[0]?.coinId ?? null, loserId: racers[0]?.coinId ?? null };
    }

    const scores = racers.map((r) => ({ coinId: r.coinId, score: parseFloat(r.performancePct) || 0 }));
    const maxScore = Math.max(...scores.map((s) => s.score));
    const minScore = Math.min(...scores.map((s) => s.score));

    const topTied = scores.filter((s) => s.score === maxScore);
    const bottomTied = scores.filter((s) => s.score === minScore);

    // Highest peak wins the top spot; fall back to earliest array index on equal peaks
    const topWinner = topTied.reduce((best, cur) => {
      const bestPeak = peakPerfRef.current.get(best.coinId) ?? best.score;
      const curPeak = peakPerfRef.current.get(cur.coinId) ?? cur.score;
      if (curPeak > bestPeak) return cur;
      if (curPeak < bestPeak) return best;
      // Equal peaks — prefer the coin that appears earlier in the original array
      return racers.findIndex((r) => r.coinId === cur.coinId) <
        racers.findIndex((r) => r.coinId === best.coinId)
        ? cur
        : best;
    });

    // Lowest trough loses; fall back to earliest array index on equal troughs
    const bottomLoser = bottomTied.reduce((best, cur) => {
      const bestTrough = troughPerfRef.current.get(best.coinId) ?? best.score;
      const curTrough = troughPerfRef.current.get(cur.coinId) ?? cur.score;
      if (curTrough < bestTrough) return cur;
      if (curTrough > bestTrough) return best;
      // Equal troughs — prefer the coin that appears earlier in the original array
      return racers.findIndex((r) => r.coinId === cur.coinId) <
        racers.findIndex((r) => r.coinId === best.coinId)
        ? cur
        : best;
    });

    return { winnerId: topWinner.coinId, loserId: bottomLoser.coinId };
  }, [racers]);

  const trackPositions = useMemo(() => getRelativePositions(racers), [racers]);

  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;

  return (
    <div className="space-y-4">
      {/* Race countdown */}
      <div className="flex items-center justify-between mb-2">
        <div className="font-bold text-xl" style={{ color: '#ff3da8' }}>
          🏁 RACE: Pump it!
        </div>
        <div
          className="text-2xl font-bold tabular-nums px-4 py-1 rounded-full"
          style={{
            background: 'rgba(255,61,168,0.15)',
            border: '2px solid rgba(255,61,168,0.4)',
            color: secondsLeft <= 30 ? '#ff3da8' : '#fbbf24',
          }}
        >
          {mins > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : `${secondsLeft}s`}
        </div>
      </div>

      {/* Mini leaderboard strip */}
      <div
        className="flex gap-2 flex-wrap p-3 rounded-2xl"
        style={{
          background: 'rgba(26,10,46,0.6)',
          border: '1px solid rgba(167,139,250,0.2)',
        }}
      >
        {sortedRacers.map((racer, rank) => {
          const pct = parseFloat(racer.performancePct) || 0;
          const color = COIN_COLORS[racers.indexOf(racer) % COIN_COLORS.length];
          const isPositive = pct >= 0;
          return (
            <div
              key={racer.coinId}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs font-semibold"
              style={{
                background: `${color}18`,
                border: `1.5px solid ${color}44`,
                color: color,
              }}
            >
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10 }}>#{rank + 1}</span>
              {racer.imageUrl && (
                <img src={racer.imageUrl} alt={racer.symbol} className="w-4 h-4 rounded-full" />
              )}
              <span>${racer.symbol}</span>
              <span style={{ color: isPositive ? '#34d399' : '#fb7185' }}>
                {isPositive ? '+' : ''}{pct.toFixed(2)}%
              </span>
            </div>
          );
        })}
      </div>

      {/* Race track */}
      <div
        className="rounded-3xl overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, rgba(15,5,30,0.95) 0%, rgba(26,10,46,0.9) 100%)',
          border: '2px solid rgba(167,139,250,0.25)',
        }}
      >
        {/* Track header stripes */}
        <div
          className="h-2"
          style={{
            background:
              'repeating-linear-gradient(90deg, #ff3da8 0, #ff3da8 20px, #fbbf24 20px, #fbbf24 40px, #34d399 40px, #34d399 60px, #60a5fa 60px, #60a5fa 80px)',
          }}
        />

        {/* Finish line marker */}
        <div className="relative px-4 pt-3 pb-2">
          {/* Grid lines */}
          <div
            className="absolute inset-x-4 top-3 bottom-0 rounded-lg opacity-10"
            style={{
              backgroundImage:
                'repeating-linear-gradient(90deg, rgba(255,255,255,0.3) 0, rgba(255,255,255,0.3) 1px, transparent 1px, transparent 12.5%)',
            }}
          />

          {/* Lanes */}
          <div className="space-y-2">
            {racers.map((racer, i) => {
              const color = COIN_COLORS[i % COIN_COLORS.length];
              const trackPos = trackPositions.get(racer.coinId) ?? 50;
              const isMyPick = userPick?.coinId === racer.coinId;
              const pct = parseFloat(racer.performancePct) || 0;
              const isPositive = pct >= 0;

              return (
                <div
                  key={racer.coinId}
                  className="flex items-center gap-0"
                  style={{
                    height: 64,
                    background: isMyPick ? 'rgba(251,191,36,0.06)' : 'transparent',
                    borderRadius: 12,
                    border: isMyPick ? '1.5px solid rgba(251,191,36,0.3)' : '1.5px solid transparent',
                  }}
                >
                  {/* Fixed-width label column — never covered by logo */}
                  <div
                    className="flex flex-col justify-center shrink-0 pl-2"
                    style={{ width: 72 }}
                  >
                    <div className="flex items-center gap-1">
                      {racer.imageUrl && (
                        <img src={racer.imageUrl} alt={racer.symbol} className="w-4 h-4 rounded-full" />
                      )}
                      <span className="text-xs font-bold truncate" style={{ color }}>
                        ${racer.symbol}
                      </span>
                    </div>
                    <div
                      className="text-xs font-bold"
                      style={{ color: isPositive ? '#34d399' : '#fb7185' }}
                    >
                      {isPositive ? '+' : ''}{pct.toFixed(2)}%
                    </div>
                  </div>

                  {/* Track lane — pulsing bar + logo */}
                  {/* pr-8 gives ≥32px right clearance so the rocket trail emoji never clips */}
                  <div className="relative flex-1 h-full flex items-center pr-8">
                    {/* Faint full-width guide line */}
                    <div
                      className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px opacity-15"
                      style={{ background: color }}
                    />

                    {/* Pulsing trail bar — extends from left to logo position */}
                    <div
                      className="absolute left-0 top-1/2 -translate-y-1/2 rounded-full"
                      style={{
                        width: `${trackPos}%`,
                        height: 4,
                        background: `linear-gradient(90deg, transparent 0%, ${color}88 40%, ${color} 100%)`,
                        boxShadow: `0 0 8px 2px ${color}55`,
                        animation: 'racePulse 1.6s ease-in-out infinite',
                        transition: 'width 1s ease-in-out',
                      }}
                    />

                    {/* Coin character — sits at end of trail */}
                    <div
                      className="absolute top-1/2 -translate-y-1/2 transition-all duration-1000 ease-in-out"
                      style={{ left: `calc(${trackPos}% - 22px)` }}
                    >
                      <CoinCharacter
                        imageUrl={racer.imageUrl}
                        symbol={racer.symbol}
                        performancePct={racer.performancePct}
                        color={color}
                        size={44}
                        isMyPick={isMyPick}
                        racePosition={raceRanks.get(racer.coinId)}
                        totalRacers={racers.length}
                        isRaceWinner={racer.coinId === winnerId}
                        isRaceLoser={racer.coinId === loserId}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Keyframe for pulsing glow */}
          <style>{`
            @keyframes racePulse {
              0%, 100% { opacity: 0.7; }
              50% { opacity: 1; box-shadow: 0 0 14px 4px currentColor; }
            }
          `}</style>
        </div>

        {/* Track footer stripes */}
        <div
          className="h-2"
          style={{
            background:
              'repeating-linear-gradient(90deg, #ff3da8 0, #ff3da8 20px, #fbbf24 20px, #fbbf24 40px, #34d399 40px, #34d399 60px, #60a5fa 60px, #60a5fa 80px)',
          }}
        />
      </div>

      {/* Pick panel — visible during race while choosing is still open */}
      <div
        className="rounded-3xl p-4 space-y-3"
        style={{
          background: 'rgba(26,10,46,0.6)',
          border: `1px solid ${choosingOpen ? 'rgba(251,191,36,0.3)' : 'rgba(167,139,250,0.2)'}`,
        }}
      >
        {!choosingOpen ? (
          <div
            className="text-center py-2.5 px-4 rounded-2xl space-y-1"
            style={{
              background: 'rgba(251,191,36,0.08)',
              border: '2px solid rgba(251,191,36,0.25)',
            }}
          >
            <div className="font-semibold text-sm" style={{ color: '#fbbf24' }}>
              🔒 Picks locked — race ending soon
            </div>
            <div className="text-xs" style={{ color: 'rgba(251,191,36,0.6)' }}>
              Picking closes 5 minutes before race end
            </div>
          </div>
        ) : userPick ? (
          <div
            className="py-2.5 rounded-2xl font-semibold"
            style={{
              background: 'rgba(251,191,36,0.1)',
              border: '2px solid rgba(251,191,36,0.5)',
            }}
          >
            <div className="text-center" style={{ color: '#fbbf24' }}>
              ✅ Your pick:{' '}
              <span className="text-white">
                ${racers.find((r) => r.coinId === userPick.coinId)?.symbol ?? userPick.coinId}
              </span>{' '}
              🚀
            </div>
            <div className="text-center mt-1 text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>
              Win payout:{' '}
              <span
                className="font-bold"
                style={{
                  color: lockedPoints >= 90 ? '#34d399' : lockedPoints >= 70 ? '#fbbf24' : '#fb7185',
                }}
              >
                +{lockedPoints} pts
              </span>
            </div>
          </div>
        ) : !user ? (
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs" style={{ color: '#a78bfa' }}>
              Still time to pick! Connect your wallet.
            </span>
            <Button
              onClick={login}
              size="sm"
              className="font-bold shrink-0"
              style={{ background: '#ff3da8', color: 'white', border: 'none' }}
            >
              🔗 Connect
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs font-semibold" style={{ color: '#fbbf24' }}>
                ⏰ Still time to pick your winner!
              </div>
              <div
                className="text-xs font-bold tabular-nums px-2.5 py-1 rounded-full shrink-0"
                style={{
                  background: 'rgba(251,191,36,0.12)',
                  border: '1.5px solid rgba(251,191,36,0.35)',
                  color: Math.max(0, secondsLeft - CHOOSING_CUTOFF) <= 60 ? '#fb7185' : '#fbbf24',
                }}
              >
                {formatPickLockCountdown(Math.max(0, secondsLeft - CHOOSING_CUTOFF))}
              </div>
            </div>

            {/* Live "Points if you win" indicator */}
            <div
              className="flex items-center justify-between px-3 py-2 rounded-xl"
              style={{
                background: pointsIfWin >= 90
                  ? 'rgba(52,211,153,0.1)'
                  : pointsIfWin >= 70
                  ? 'rgba(251,191,36,0.08)'
                  : 'rgba(251,113,133,0.08)',
                border: pointsIfWin >= 90
                  ? '1.5px solid rgba(52,211,153,0.35)'
                  : pointsIfWin >= 70
                  ? '1.5px solid rgba(251,191,36,0.25)'
                  : '1.5px solid rgba(251,113,133,0.25)',
              }}
            >
              <span className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>
                ⚡ Pick now to win
              </span>
              <span
                className="text-base font-extrabold tabular-nums"
                style={{
                  color: pointsIfWin >= 90 ? '#34d399' : pointsIfWin >= 70 ? '#fbbf24' : '#fb7185',
                  letterSpacing: '-0.02em',
                }}
              >
                +{pointsIfWin} pts
              </span>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {racers.map((racer, i) => (
                <button
                  key={racer.coinId}
                  onClick={() => handlePick(racer.coinId)}
                  disabled={picking}
                  className="flex items-center gap-1.5 px-2.5 py-2 rounded-xl font-semibold text-xs transition-all hover:scale-105 active:scale-95 disabled:opacity-50"
                  style={{
                    background: `${COIN_COLORS[i % COIN_COLORS.length]}22`,
                    border: `2px solid ${COIN_COLORS[i % COIN_COLORS.length]}66`,
                    color: COIN_COLORS[i % COIN_COLORS.length],
                  }}
                >
                  {racer.imageUrl && (
                    <img src={racer.imageUrl} alt={racer.symbol} className="w-5 h-5 rounded-full object-cover" />
                  )}
                  <span>${racer.symbol}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
